import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { CRMSearch } from '../helper'
import dayjs from 'dayjs'
import { coreCompetencies, salesforceForecastCategories, salesforceStages, setAsides } from '../lists'
import './NewQuote.css'
import { usePaymentTerms, useRepCodes, useUser } from '../hooks'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { quoteType, salesforceOpportunityType } from '../types'
import { DatePicker } from '@impres-development/assets/client'
import { CatalogRow } from 'src/CatalogList/CatalogList.types'

export default function NewQuoteDialog(props: {
  open: boolean
  onClose: () => void
  onCreated?: (quoteId: string) => Promise<void>
}) {
  const user = useUser()
  const [repCodes] = useRepCodes()
  const paymentTerms = usePaymentTerms()
  const navigate = useNavigate()

  const [newQuote, setNewQuote] = useState<Partial<quoteType>>({
    RepCode: '',
    Name: '',
    RFQNumber: '',
    ContractVehicle: '',
    ContractNumber: '',
    ExpirationDate: dayjs().add(30, 'day').toDate() as Date | null,
    RFQDueDate: null as Date | null,
    QuoteDescription: '',
    GovPayTerms: '',
    Status: 'Draft',
    QuoteDate: dayjs().toDate(),
    CRMAccount: null,
    CRMContact: null,
    CRMOpportunity: null,
  })
  const [linkToOpportunity, setLinkToOpportunity] = useState(true)
  const [existingOpportunity, setExistingOpportunity] = useState(true)
  const [selectedOpportunity, setSelectedOpportunity] = useState<{ id: string; label: string } | null>(null)
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; label: string } | null>(null)
  const [selectedContact, setSelectedContact] = useState<{ id: string; label: string } | null>(null)
  const [newOpportunity, setNewOpportunity] = useState<Partial<salesforceOpportunityType>>({
    Name: '',
    CloseDate: null,
    StageName: '',
    ForecastCategoryName: '',
    Line_Of_Business__c: [],
    Set_Asides__c: [],
  })
  const [loading, setLoading] = useState(false)
  const [fieldMissingError, setFieldMissingError] = useState({
    id: false,
    name: false,
    closeDate: false,
    stageName: false,
    forecastCategory: false,
    coreCompetency: false,
    setAsides: false,
    accountId: false,
  })
  const [createOppError, setCreateOppError] = useState(false)
  const [createQuoteError, setCreateQuoteError] = useState(false)
  const [createSalesforceQuoteError, setCreateSalesforceQuoteError] = useState(false)
  const [contractList, setContractList] = useState<CatalogRow[]>([])

  useEffect(() => {
    if (!user.email) return

    axios.get('/contract').then(res => {
      setContractList(
        res.data.list
          .filter((c: CatalogRow) => c.status === 'Active' && c.parentId === null)
          .sort((a: CatalogRow, b: CatalogRow) => a.contractVehicle.localeCompare(b.contractVehicle)),
      )
    })
  }, [user])

  useEffect(() => {
    if (newQuote.QuoteDate) {
      setNewQuote(prev => ({
        ...prev,
        ExpirationDate: dayjs(newQuote.QuoteDate).add(30, 'day').toDate(),
      }))
    }
  }, [newQuote.QuoteDate])

  const handleClose = () => {
    props.onClose()
    setNewQuote({
      RepCode: '',
      ContractVehicle: '',
      ContractNumber: '',
      ExpirationDate: dayjs().add(30, 'day').toDate(),
      RFQNumber: '',
      RFQDueDate: null,
      QuoteDescription: '',
      GovPayTerms: '',
      Status: 'Draft',
      QuoteDate: dayjs().toDate(),
      Name: '',
      CRMAccount: null,
      CRMContact: null,
      CRMOpportunity: null,
    })
    setExistingOpportunity(true)
    setSelectedOpportunity(null)
    setSelectedAccount(null)
    setSelectedContact(null)
    setNewOpportunity({
      Name: '',
      CloseDate: null,
      StageName: '',
      ForecastCategoryName: '',
      Line_Of_Business__c: [],
      Set_Asides__c: [],
    })
    setFieldMissingError({
      id: false,
      name: false,
      closeDate: false,
      stageName: false,
      forecastCategory: false,
      coreCompetency: false,
      setAsides: false,
      accountId: false,
    })
    setCreateOppError(false)
    setCreateQuoteError(false)
    setCreateSalesforceQuoteError(false)
  }

  const handleCreateNewQuote = () => {
    const missingFields = {
      id: !selectedOpportunity,
      name: !newOpportunity.Name,
      closeDate: !newOpportunity.CloseDate,
      stageName: !newOpportunity.StageName,
      forecastCategory: !newOpportunity.ForecastCategoryName,
      coreCompetency: newOpportunity.Line_Of_Business__c?.length === 0,
      setAsides: newOpportunity.Set_Asides__c?.length === 0,
      accountId: !selectedAccount,
    }
    setFieldMissingError(missingFields)

    if (!newQuote.CRMAccount) {
      return
    }

    if (linkToOpportunity && existingOpportunity && !newQuote.CRMOpportunity) {
      setExistingOpportunity(false)
      setNewOpportunity({
        Name: '',
        CloseDate: null,
        StageName: '',
        ForecastCategoryName: '',
        Line_Of_Business__c: [],
        Set_Asides__c: [],
      })
      return
    }

    if (
      linkToOpportunity &&
      !existingOpportunity &&
      (missingFields.name ||
        missingFields.closeDate ||
        missingFields.stageName ||
        missingFields.forecastCategory ||
        missingFields.coreCompetency ||
        missingFields.setAsides)
    ) {
      return
    }

    setLoading(true)
    setCreateOppError(false)
    setCreateQuoteError(false)
    setCreateSalesforceQuoteError(false)
    axios
      .post('/quote', {
        quote: newQuote,
        newOpportunity:
          !linkToOpportunity || existingOpportunity
            ? undefined
            : { ...newOpportunity, AccountId: newQuote.CRMAccount, Owner: { Email: user.email } },
        salesforceQuote: {
          ContactId: newQuote.CRMContact,
        },
      })
      .then(async res => {
        handleClose()
        if (props.onCreated) {
          try {
            await props.onCreated(res.data)
          } catch (err) {
            console.error('onCreated callback failed', err)
          }
        }
        navigate(`/quote/${res.data}`)
      })
      .catch(async err => {
        console.error(err)
        if (err.response.data.message === 'Failed to create opportunity') {
          setCreateOppError(true)
        } else if (err.response.data.message === 'Failed to create quote') {
          setCreateQuoteError(true)
          if (!existingOpportunity) {
            setExistingOpportunity(true)
            setSelectedOpportunity({
              id: err.response.data.opportunityId,
              label: newOpportunity.Name ?? 'New Opportunity ' + dayjs().format('MM/DD/YYYY'),
            })
            setNewQuote(prev => ({
              ...prev,
              CRMOpportunity: err.response.data.opportunityId,
            }))
          }
        } else if (err.response.data.message === 'Failed to create Salesforce quote') {
          const quoteId = err.response.data.quoteId
          handleClose()
          setCreateSalesforceQuoteError(true)
          if (props.onCreated) {
            try {
              await props.onCreated(quoteId)
            } catch (onCreatedErr) {
              console.error('onCreated callback failed', onCreatedErr)
            }
          }
          navigate(`/quote/${quoteId}`)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog fullWidth open={props.open} onClose={handleClose} maxWidth="lg">
        <DialogTitle>New Quote</DialogTitle>
        <DialogContent className="new-quote-dialog-content">
          <Typography>Fields marked with a * are required.</Typography>
          <Typography variant="subtitle2" fontWeight="bold">
            Header Information
          </Typography>
          <Grid container spacing="16px" width="100%">
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Contract Vehicle"
                size="small"
                value={newQuote.ContractVehicle}
                onChange={e =>
                  setNewQuote(prev => ({
                    ...prev,
                    ContractVehicle: e.target.value,
                    ContractNumber: contractList.find(c => c.contractVehicle === e.target.value)?.contractNumber ?? '',
                  }))
                }
              >
                {contractList.map(contract => (
                  <MenuItem value={contract.contractVehicle} key={contract.contractVehicle}>
                    {contract.contractVehicle}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <CRMSearch
                object="Account"
                label="CRM Account *"
                onChange={(e, value) => {
                  setSelectedAccount(value)
                  setSelectedOpportunity(null)
                  setNewQuote(prev => ({ ...prev, CRMAccount: value?.id, CRMContact: null, CRMOpportunity: null }))
                }}
                value={selectedAccount}
                error={fieldMissingError.accountId}
                errorText="Account is required"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Rep Code"
                size="small"
                value={newQuote.RepCode}
                onChange={e =>
                  setNewQuote(prev => ({
                    ...prev,
                    RepCode: e.target.value,
                    Owner: repCodes.find(repCode => repCode.RepCode === e.target.value)?.IsrEmail ?? null,
                  }))
                }
              >
                {repCodes
                  .filter(
                    code =>
                      code.IsActive &&
                      (code.IsrEmail?.toLowerCase() === user.email?.toLowerCase() ||
                        code.AeEmail?.toLowerCase() === user.email?.toLowerCase()),
                  )
                  .map(code => (
                    <MenuItem value={code.RepCode} key={code.RepCode}>
                      {code.RepCode}{' '}
                      {[code.InsideSalesRep, code.AccountExecutive, code.Engineer].filter(Boolean).join(', ')}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <CRMSearch
                object="Contact"
                label="CRM Contact"
                accountId={selectedAccount?.id}
                onChange={(e, value) => {
                  setSelectedContact(value)
                  setNewQuote(prev => ({ ...prev, CRMContact: value?.id }))
                }}
                value={selectedContact}
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" fontWeight="bold">
            Additional Header Details
          </Typography>
          <Grid container spacing="16px" width="100%">
            <Grid size={6}>
              <TextField
                fullWidth
                label="Quote Name"
                size="small"
                value={newQuote.Name}
                onChange={e => setNewQuote(prev => ({ ...prev, Name: e.target.value }))}
                slotProps={{ htmlInput: { maxLength: 250 } }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Quote Description"
                size="small"
                value={newQuote.QuoteDescription}
                onChange={e => setNewQuote(prev => ({ ...prev, QuoteDescription: e.target.value }))}
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                label="Quote Date"
                size="small"
                value={newQuote.QuoteDate ? dayjs(newQuote.QuoteDate) : null}
                onChange={e => setNewQuote(prev => ({ ...prev, QuoteDate: e ? dayjs(e).toDate() : null }))}
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                label="Expiration Date"
                size="small"
                value={newQuote.ExpirationDate ? dayjs(newQuote.ExpirationDate) : null}
                onChange={e => setNewQuote(prev => ({ ...prev, ExpirationDate: e ? dayjs(e).toDate() : null }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="RFQ #"
                size="small"
                value={newQuote.RFQNumber}
                onChange={e => setNewQuote(prev => ({ ...prev, RFQNumber: e.target.value }))}
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                label="RFQ Due Date"
                size="small"
                value={newQuote.RFQDueDate ? dayjs(newQuote.RFQDueDate) : null}
                onChange={e => setNewQuote(prev => ({ ...prev, RFQDueDate: e ? dayjs(e).toDate() : null }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                select
                label="Terms"
                size="small"
                value={newQuote.GovPayTerms}
                onChange={e => setNewQuote(prev => ({ ...prev, GovPayTerms: e.target.value }))}
              >
                {paymentTerms.map(term => (
                  <MenuItem value={term.name} key={term.id}>
                    {term.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <div className="opportunity-container">
            <div>
              <Typography>Do you want to link this new quote to a CRM opportunity at this time?</Typography>
              <RadioGroup
                row
                value={linkToOpportunity}
                onChange={e => {
                  setLinkToOpportunity(e.target.value === 'true')
                  if (e.target.value === 'false') {
                    setNewQuote(prev => ({
                      ...prev,
                      CRMOpportunity: null,
                    }))
                    setSelectedOpportunity(null)
                  }
                }}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </div>
            {linkToOpportunity && (
              <>
                <div>
                  <Typography>Is there an existing opportunity?</Typography>
                  <RadioGroup
                    row
                    value={existingOpportunity}
                    onChange={e => {
                      setExistingOpportunity(e.target.value === 'true')
                      setNewQuote(prev => ({
                        ...prev,
                        CRMOpportunity: e.target.value === 'false' ? null : selectedOpportunity?.id,
                      }))
                    }}
                  >
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio />} label="Create New" />
                  </RadioGroup>
                </div>
                <Typography>
                  Opportunities will only show for the CRM Account selected. Clicking the create button will create a
                  new opportunity in the CRM.
                </Typography>
                {existingOpportunity ? (
                  <Grid container spacing="16px" width="100%">
                    <Grid size={6}>
                      <CRMSearch
                        object="Opportunity"
                        label="Opportunity Name"
                        accountId={selectedAccount?.id}
                        onChange={(e, value) => {
                          setSelectedOpportunity(value)
                          setNewQuote(prev => ({ ...prev, CRMOpportunity: value?.id }))
                        }}
                        disabled={!selectedAccount}
                        value={selectedOpportunity}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing="16px" width="100%">
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        error={fieldMissingError.name}
                        helperText={fieldMissingError.name ? 'Opportunity is required' : ''}
                        label="Opportunity Name"
                        size="small"
                        value={newOpportunity.Name}
                        onChange={e => setNewOpportunity(prev => ({ ...prev, Name: e.target.value }))}
                      />
                    </Grid>
                    <Grid size={6}>
                      <DatePicker
                        fullWidth
                        error={fieldMissingError.closeDate}
                        helperText={fieldMissingError.closeDate ? 'Close Date is required' : ''}
                        label="Close Date"
                        size="small"
                        value={newOpportunity.CloseDate ? dayjs(newOpportunity.CloseDate) : null}
                        onChange={e =>
                          setNewOpportunity(prev => ({ ...prev, CloseDate: e ? dayjs(e).toDate() : null }))
                        }
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        select
                        error={fieldMissingError.stageName}
                        helperText={fieldMissingError.stageName ? 'Stage is required' : ''}
                        label="Stage"
                        size="small"
                        value={newOpportunity.StageName}
                        onChange={e => setNewOpportunity(prev => ({ ...prev, StageName: e.target.value }))}
                      >
                        {salesforceStages.map(stage => (
                          <MenuItem value={stage} key={stage}>
                            {stage}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        select
                        label="Forecast Category"
                        size="small"
                        value={newOpportunity.ForecastCategoryName}
                        onChange={e => setNewOpportunity(prev => ({ ...prev, ForecastCategoryName: e.target.value }))}
                        error={fieldMissingError.forecastCategory}
                        helperText={fieldMissingError.forecastCategory ? 'Forecast Category is required' : ''}
                      >
                        {salesforceForecastCategories.map(category => (
                          <MenuItem value={category} key={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        select
                        error={fieldMissingError.coreCompetency}
                        helperText={fieldMissingError.coreCompetency ? 'Core Competency is required' : ''}
                        label="Core Competency"
                        size="small"
                        slotProps={{ select: { multiple: true } }}
                        value={newOpportunity.Line_Of_Business__c}
                        onChange={e =>
                          setNewOpportunity(prev => ({
                            ...prev,
                            Line_Of_Business__c:
                              typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                          }))
                        }
                      >
                        {coreCompetencies.map(competency => (
                          <MenuItem value={competency} key={competency}>
                            {competency}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        select
                        error={fieldMissingError.setAsides}
                        helperText={fieldMissingError.setAsides ? 'Set Asides is required' : ''}
                        label="Set Asides"
                        size="small"
                        slotProps={{ select: { multiple: true } }}
                        value={newOpportunity.Set_Asides__c}
                        onChange={e =>
                          setNewOpportunity(prev => ({
                            ...prev,
                            Set_Asides__c:
                              typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                          }))
                        }
                      >
                        {setAsides.map(aside => (
                          <MenuItem value={aside} key={aside}>
                            {aside}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={6}>
                      <CRMSearch
                        object="Account"
                        label="CRM Account"
                        onChange={(e, value) => {
                          setSelectedAccount(value)
                          setSelectedContact(null)
                        }}
                        value={selectedAccount}
                        error={fieldMissingError.accountId}
                        errorText="Account is required"
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </div>
          <Snackbar open={createOppError}>
            <Alert severity="error" variant="filled" onClose={() => setCreateOppError(false)}>
              Failed to create opportunity. Please try again or contact support if the problem persists.
            </Alert>
          </Snackbar>
          <Snackbar open={createQuoteError}>
            <Alert severity="error" variant="filled" onClose={() => setCreateQuoteError(false)}>
              Failed to create quote. Please try again or contact support if the problem persists.
            </Alert>
          </Snackbar>
        </DialogContent>
        <DialogActions className="new-quote-dialog-actions">
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateNewQuote} loading={loading}>
            Create New Quote
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={createSalesforceQuoteError}>
        <Alert severity="error" variant="filled" onClose={() => setCreateSalesforceQuoteError(false)}>
          Salesforced sync failed. It will retry automatically when you make changes. If this keeps happpening, please
          contact support.
        </Alert>
      </Snackbar>
    </>
  )
}
